<template>
  <div>
    <!-- Header Fixo -->
    <b-card no-body class="mb-2">
      <div class="m-2 d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-0">
            {{ tableData.name }}
            <b-badge variant="light-primary" class="ml-1">
              {{ tableData.itemCount || 0 }} {{ $t('itens') }}
            </b-badge>
            <b-badge variant="light-info" class="ml-50">
              {{ (tableData.applications || []).length }} {{ $t('modelos') }}
            </b-badge>
          </h3>
          <p class="text-muted mt-50 mb-0">{{ tableData.description }}</p>
        </div>
        <div>
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="$router.push({ name: 'dtc-list' })"
          >
            {{ $t('Voltar') }}
          </b-button>
          <b-button
            v-if="tableData.active"
            variant="danger"
            @click="confirmInactivate"
          >
            {{ $t('Inativar') }}
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Abas -->
    <b-card no-body>
      <b-tabs pills class="nav-pill-secondary m-1" v-model="tabIndex">
        <!-- Aba A: Geral -->
        <b-tab :title="$t('Geral')">
          <div class="mt-2">
            <validation-observer ref="generalForm">
              <b-form @submit.prevent="updateMeta">
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group :label="$t('Nome') + '*'" label-for="name">
                      <validation-provider #default="{ errors }" name="name" rules="required">
                        <b-form-input
                          id="name"
                          v-model="editMeta.name"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group :label="$t('Descrição')" label-for="description">
                      <b-form-input
                        id="description"
                        v-model="editMeta.description"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <b-button type="submit" variant="primary" :disabled="isUpdatingMeta">
                      <b-spinner v-if="isUpdatingMeta" small class="mr-50" />
                      {{ $t('Salvar alterações') }}
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </div>
        </b-tab>

        <!-- Aba B: Modelos Vinculados -->
        <b-tab :title="$t('Modelos Vinculados')">
          <div class="mt-2">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h5 class="mb-0">{{ $t('Modelos associados') }}</h5>
              <b-button variant="primary" size="sm" @click="openLinkModal">
                <feather-icon icon="LinkIcon" class="mr-50" />
                {{ $t('Vincular modelos') }}
              </b-button>
            </div>
            
            <b-table
              :items="tableData.applications || []"
              :fields="modelColumns"
              responsive
              show-empty
              :empty-text="$t('Nenhum modelo. Vincule para que ativos desse modelo resolvam DTCs.')"
            >
              <template #cell(actions)="data">
                <b-button
                  variant="flat-danger"
                  class="btn-icon"
                  v-b-tooltip.hover.top="$t('Remover vínculo')"
                  @click="confirmUnlink(data.item)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </template>
            </b-table>
          </div>
        </b-tab>

        <!-- Aba C: Códigos DTC -->
        <b-tab :title="$t('Códigos DTC')">
          <div class="mt-2">
            <dtc-items-tab :dtc-table-id="tableId" @update-count="updateItemCount" />
          </div>
        </b-tab>
      </b-tabs>
    </b-card>

    <model-link-modal ref="modelLinkModal" :dtc-table-id="tableId" @linked="fetchTableDetail" />
  </div>
</template>

<script>
import {
  BCard, BTabs, BTab, BRow, BCol, BFormGroup, BFormInput, BForm, BButton, BBadge, BAlert, BTable, BSpinner, VBTooltip
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import store from '@/store'
import dtcStoreModule from './dtcStoreModule'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DtcItemsTab from '@/views/main/dtc/components/DtcItemsTab.vue'
import ModelLinkModal from '@/views/main/dtc/components/ModelLinkModal.vue'

export default {
  components: {
    BCard, BTabs, BTab, BRow, BCol, BFormGroup, BFormInput, BForm, BButton, BBadge, BAlert, BTable, BSpinner,
    ValidationProvider, ValidationObserver,
    DtcItemsTab, ModelLinkModal
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  data() {
    return {
      tableId: this.$route.params.id,
      tabIndex: 0,
      tableData: {
        name: '',
        description: '',
        itemCount: 0,
        applications: [],
        active: true
      },
      editMeta: {
        name: '',
        description: ''
      },
      isUpdatingMeta: false,
      modelColumns: [
        { key: 'assetManufacturerName', label: this.$t('Fabricante') },
        { key: 'assetModelName', label: this.$t('Modelo') },
        { key: 'actions', label: this.$t('Ações') },
      ],
      required
    }
  },
  created() {
    const DTC_APP_STORE_MODULE_NAME = 'app-dtc'
    if (!store.hasModule(DTC_APP_STORE_MODULE_NAME)) {
      store.registerModule(DTC_APP_STORE_MODULE_NAME, dtcStoreModule)
    }

    if (this.$route.query.tab === 'items') {
      this.tabIndex = 2
    } else if (this.$route.query.tab === 'models') {
      this.tabIndex = 1
    }

    this.fetchTableDetail()
  },
  methods: {
    fetchTableDetail() {
      store.dispatch('app-dtc/fetchTableDetail', { id: this.tableId })
        .then(response => {
          this.tableData = response.data || {}
          this.editMeta.name = this.tableData.name
          this.editMeta.description = this.tableData.description
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao carregar tabela'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        })
    },
    async updateMeta() {
      const success = await this.$refs.generalForm.validate()
      if (success) {
        this.isUpdatingMeta = true
        
        const payload = {
          id: this.tableId,
          name: this.editMeta.name,
          description: this.editMeta.description,
          items: [],
          applications: []
        }

        try {
          await store.dispatch('app-dtc/updateTable', payload)
          this.tableData.name = payload.name
          this.tableData.description = payload.description
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Atualizado com sucesso'), icon: 'CheckIcon', variant: 'success' }
          })
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao atualizar'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        } finally {
          this.isUpdatingMeta = false
        }
      }
    },
    openLinkModal() {
      this.$refs.modelLinkModal.show()
    },
    async confirmUnlink(model) {
      const result = await this.$swal({
        title: this.$t('Remover vínculo?'),
        text: this.$t(`Tem certeza que deseja remover o modelo ${model.assetModelName}?`),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Sim, remover'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      
      if (result && result.value) {
        try {
          await store.dispatch('app-dtc/unlinkModel', { dtcTableId: this.tableId, assetModelId: model.assetModelId })
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Vínculo removido'), icon: 'CheckIcon', variant: 'success' }
          })
          this.fetchTableDetail()
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao remover vínculo'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        }
      }
    },
    async confirmInactivate() {
      const result = await this.$swal({
        title: this.$t('Você tem certeza?'),
        text: this.$t('Ao inativar esta tabela, os modelos vinculados ficarão sem cobertura até que sejam religados a uma nova tabela.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Sim, inativar'),
        cancelButtonText: this.$t('Cancelar'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      
      if (result && result.value) {
        try {
          await store.dispatch('app-dtc/inactivateTable', { id: this.tableId })
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Tabela inativada'), icon: 'CheckIcon', variant: 'success' }
          })
          this.$router.push({ name: 'dtc-list' })
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao inativar'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        }
      }
    },
    updateItemCount(count) {
      this.tableData.itemCount = count
    }
  }
}
</script>

<style scoped>
.btn-icon {
  padding: 0.5rem;
}
</style>
